import { useEffect, useState, useCallback } from 'react';
import { api, ApiError } from '../lib/api';
import { Skel } from '../components/Skel';
import { Banner } from '../components/Banner';
import { EmptyState } from '../components/EmptyState';
import { RiskBadge } from '../components/RiskBadge';
import { Crumbs } from '../components/Crumbs';
import type { FacetsResponse, RiskLevel, CompanyStatus } from '../data/types';

interface SearchResult {
  slug: string;
  inn: string;
  ogrn?: string;
  name_short: string;
  name_full?: string;
  status: CompanyStatus;
  region_code?: string | null;
  region_name?: string | null;
  okved_main_code?: string | null;
  okved_main_name?: string | null;
  risk_score?: number | null;
  risk_level?: RiskLevel | null;
  last_revenue?: string | null;
  rating?: string | null;
  // от searchAdvanced — флаги реестров
  has_minsvyaz_software?: boolean;
  has_certs?: boolean;
  has_sro?: boolean;
}

interface SearchProps {
  /** Initial query (например, из URL ?q=...). Если пусто — показываются глобальные фасеты. */
  initialQuery?: string;
}

/**
 * Страница поиска компаний с фасет-фильтрами в сайдбаре.
 *
 * Fetches:
 *  - POST /v1/companies/search — items
 *  - GET /v1/companies/facets?q= — фильтры (regions/okveds/risk_levels/statuses)
 *
 * URL state — простая интеграция через hash (без react-router пока, чтобы
 * не тащить ещё одну зависимость до миграции). Для C3-followup — добавить
 * react-router-dom + useSearchParams.
 *
 * Mobile-first: sidebar collapse под 720px, results stacked.
 */
export function Search({ initialQuery = '' }: SearchProps) {
  const [query, setQuery] = useState(initialQuery);
  const [submitted, setSubmitted] = useState(initialQuery);
  const [page, setPage] = useState(1);
  const [results, setResults] = useState<SearchResult[] | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [facets, setFacets] = useState<FacetsResponse | null>(null);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedRiskLevels, setSelectedRiskLevels] = useState<string[]>([]);
  // Boolean фильтры по реестрам
  const [hasMinsvyaz, setHasMinsvyaz] = useState(false);
  const [hasCerts, setHasCerts] = useState(false);
  const [hasSro, setHasSro] = useState(false);
  const [hasSanctions, setHasSanctions] = useState(false);

  const performSearch = useCallback(
    (
      q: string,
      p = 1,
      regions: string[] = [],
      risks: string[] = [],
      flags: {
        minsvyaz?: boolean;
        certs?: boolean;
        sro?: boolean;
        sanctions?: boolean;
      } = {}
    ) => {
      const useAdvanced =
        flags.minsvyaz || flags.certs || flags.sro || flags.sanctions;

      // Без q и без флагов — показывать пустой state
      if ((!q || q.length < 2) && !useAdvanced) {
        setResults(null);
        setTotal(0);
        return;
      }
      setLoading(true);
      setError(null);
      const ac = new AbortController();
      const promise = useAdvanced
        ? api.searchAdvanced(
            {
              q: q || undefined,
              page: p,
              per_page: 20,
              ...(regions.length ? { region: regions } : {}),
              ...(risks.length ? { risk: risks } : {}),
              has_minsvyaz_software: flags.minsvyaz,
              has_certificates: flags.certs,
              has_sro_membership: flags.sro,
              has_sanctions: flags.sanctions,
            },
            { signal: ac.signal }
          )
        : api.search(
            {
              q,
              page: p,
              per_page: 20,
              ...(regions.length ? { region: regions } : {}),
              ...(risks.length ? { risk: risks } : {}),
            } as { q: string; page: number; per_page: number; region?: string[] },
            { signal: ac.signal }
          );

      promise
        .then((r) => {
          const items = (r.items as SearchResult[]) ?? [];
          setResults(items);
          setTotal(r.total);
        })
        .catch((e) => {
          if (e.name !== 'AbortError') {
            setError(e instanceof ApiError ? `API ${e.status}` : String(e));
          }
        })
        .finally(() => setLoading(false));
      return () => ac.abort();
    },
    []
  );

  // Initial search + facets fetch
  useEffect(() => {
    if (initialQuery) performSearch(initialQuery, 1);
  }, [initialQuery, performSearch]);

  useEffect(() => {
    const ac = new AbortController();
    api
      .facets(submitted || undefined, { signal: ac.signal })
      .then(setFacets)
      .catch(() => {});
    return () => ac.abort();
  }, [submitted]);

  function currentFlags() {
    return {
      minsvyaz: hasMinsvyaz,
      certs: hasCerts,
      sro: hasSro,
      sanctions: hasSanctions,
    };
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitted(query);
    setPage(1);
    performSearch(query, 1, selectedRegions, selectedRiskLevels, currentFlags());
  }

  function toggleRegion(code: string) {
    const next = selectedRegions.includes(code)
      ? selectedRegions.filter((x) => x !== code)
      : [...selectedRegions, code];
    setSelectedRegions(next);
    if (submitted || hasMinsvyaz || hasCerts || hasSro || hasSanctions) {
      performSearch(submitted, 1, next, selectedRiskLevels, currentFlags());
    }
  }

  function toggleRisk(level: string) {
    const next = selectedRiskLevels.includes(level)
      ? selectedRiskLevels.filter((x) => x !== level)
      : [...selectedRiskLevels, level];
    setSelectedRiskLevels(next);
    if (submitted || hasMinsvyaz || hasCerts || hasSro || hasSanctions) {
      performSearch(submitted, 1, selectedRegions, next, currentFlags());
    }
  }

  function toggleRegistry(
    key: 'minsvyaz' | 'certs' | 'sro' | 'sanctions',
    setter: (v: boolean) => void,
    current: boolean
  ) {
    const newValue = !current;
    setter(newValue);
    const flags = { ...currentFlags(), [key]: newValue };
    performSearch(submitted, 1, selectedRegions, selectedRiskLevels, flags);
  }

  return (
    <main className="container" style={{ paddingTop: 32 }}>
      <Crumbs
        items={[
          { label: 'Главная', href: '#/' },
          { label: 'Поиск' },
        ]}
      />

      <h1 style={{ marginTop: 16, marginBottom: 16 }}>Поиск компаний</h1>

      {/* SEARCH FORM */}
      <form onSubmit={onSubmit} style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="ИНН, ОГРН, название"
            aria-label="Поисковый запрос"
            style={{
              flex: '1 1 280px',
              minWidth: 0,
              padding: '12px 16px',
              fontSize: 15,
              border: '1px solid var(--border, #e6e2d6)',
              borderRadius: 8,
              background: 'var(--surface, #ffffff)',
            }}
          />
          <button
            type="submit"
            style={{
              padding: '12px 24px',
              background: 'var(--navy-700, #0b1736)',
              color: '#ffffff',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              fontSize: 15,
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            Найти
          </button>
        </div>
      </form>

      {/* GRID: facets sidebar + results */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr)',
          gap: 24,
        }}
        className="search-grid"
      >
        <style>{`
          @media (min-width: 720px) {
            .search-grid {
              grid-template-columns: 240px minmax(0, 1fr);
            }
          }
        `}</style>

        {/* FACETS */}
        <aside aria-label="Фильтры">
          {!facets ? (
            <Skel count={4} height={32} />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Реестр-флаги */}
              <div>
                <h3
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'var(--navy-700, #0b1736)',
                    margin: '0 0 8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                  }}
                >
                  Реестры
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <RegistryToggle
                    label="🔵 Российское ПО"
                    note="Минцифры реестр"
                    count={facets.registries?.has_minsvyaz}
                    on={hasMinsvyaz}
                    onChange={() => toggleRegistry('minsvyaz', setHasMinsvyaz, hasMinsvyaz)}
                  />
                  <RegistryToggle
                    label="🟢 Сертификаты"
                    note="Росаккредитация"
                    count={facets.registries?.has_certificates}
                    on={hasCerts}
                    onChange={() => toggleRegistry('certs', setHasCerts, hasCerts)}
                  />
                  <RegistryToggle
                    label="🟣 В СРО"
                    note="НОПРИЗ изыскатели"
                    count={facets.registries?.has_sro}
                    on={hasSro}
                    onChange={() => toggleRegistry('sro', setHasSro, hasSro)}
                  />
                  <RegistryToggle
                    label="⚠ Под санкциями"
                    note="OFAC/EU/UK/UN/+12 списков"
                    count={undefined}
                    on={hasSanctions}
                    onChange={() => toggleRegistry('sanctions', setHasSanctions, hasSanctions)}
                  />
                </ul>
              </div>
              {facets.regions.length > 0 && (
                <FacetGroup
                  title="Регион"
                  items={facets.regions.map((r) => ({
                    code: r.code,
                    label: r.name ?? r.code,
                    count: r.count,
                  }))}
                  selected={selectedRegions}
                  onToggle={toggleRegion}
                />
              )}
              {facets.risk_levels.length > 0 && (
                <FacetGroup
                  title="Уровень риска"
                  items={facets.risk_levels.map((r) => ({
                    code: r.level,
                    label: RISK_LEVEL_LABELS[r.level] ?? r.level,
                    count: r.count,
                  }))}
                  selected={selectedRiskLevels}
                  onToggle={toggleRisk}
                />
              )}
              {facets.scope === 'query' && typeof facets.matched_count === 'number' && (
                <p style={{ fontSize: 13, color: 'var(--muted, #6b7287)' }}>
                  Сматчилось: {facets.matched_count.toLocaleString('ru-RU')}
                </p>
              )}
            </div>
          )}
        </aside>

        {/* RESULTS */}
        <section aria-label="Результаты">
          {error && <Banner tone="risk" title="Ошибка поиска">{error}</Banner>}

          {!submitted && !error && (
            <EmptyState
              icon="🔍"
              title="Введите запрос"
              body="ИНН (10 или 12 цифр), ОГРН или название компании. Минимум 2 символа."
            />
          )}

          {submitted && loading && (
            <div style={{ display: 'grid', gap: 12 }}>
              <Skel count={5} height={88} radius={12} />
            </div>
          )}

          {submitted && !loading && results && results.length === 0 && (
            <EmptyState
              icon="🤷"
              title="Ничего не найдено"
              body={`По запросу «${submitted}» компаний нет. Попробуйте сменить фильтры или укоротить запрос.`}
            />
          )}

          {submitted && !loading && results && results.length > 0 && (
            <>
              <p style={{ color: 'var(--muted, #6b7287)', fontSize: 14, marginBottom: 12 }}>
                Найдено: {total.toLocaleString('ru-RU')}
              </p>
              <ul
                style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                }}
              >
                {results.map((c) => (
                  <li
                    key={c.slug}
                    style={{
                      background: 'var(--surface, #ffffff)',
                      border: '1px solid var(--border, #e6e2d6)',
                      borderRadius: 12,
                      padding: 16,
                      minWidth: 0,
                    }}
                  >
                    <a
                      href={`#/company/${c.slug}`}
                      style={{
                        fontWeight: 600,
                        fontSize: 16,
                        color: 'var(--navy-700, #0b1736)',
                        textDecoration: 'none',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {c.name_short}
                    </a>
                    <div
                      style={{
                        fontSize: 13,
                        color: 'var(--muted, #6b7287)',
                        margin: '6px 0',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      ИНН {c.inn}
                      {c.region_name && ` · ${c.region_name}`}
                      {c.status !== 'active' && ` · ${c.status}`}
                    </div>
                    {c.okved_main_name && (
                      <div
                        style={{
                          fontSize: 13,
                          color: 'var(--muted, #6b7287)',
                          marginBottom: 8,
                          overflowWrap: 'anywhere',
                        }}
                      >
                        ОКВЭД {c.okved_main_code}: {c.okved_main_name}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <RiskBadge level={c.risk_level ?? null} score={c.risk_score} />
                      {c.has_minsvyaz_software && (
                        <RegistryBadge tone="blue" label="🔵 ПО Минцифры" />
                      )}
                      {c.has_certs && (
                        <RegistryBadge tone="green" label="🟢 Сертификаты" />
                      )}
                      {c.has_sro && <RegistryBadge tone="purple" label="🟣 СРО" />}
                    </div>
                  </li>
                ))}
              </ul>

              {total > 20 && (
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    marginTop: 16,
                    justifyContent: 'center',
                  }}
                >
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => {
                      const np = page - 1;
                      setPage(np);
                      performSearch(submitted, np, selectedRegions, selectedRiskLevels);
                    }}
                    style={pagBtn(page <= 1)}
                  >
                    ← Назад
                  </button>
                  <span style={{ alignSelf: 'center', color: 'var(--muted, #6b7287)' }}>
                    Стр. {page} из {Math.ceil(total / 20)}
                  </span>
                  <button
                    type="button"
                    disabled={page >= Math.ceil(total / 20)}
                    onClick={() => {
                      const np = page + 1;
                      setPage(np);
                      performSearch(submitted, np, selectedRegions, selectedRiskLevels);
                    }}
                    style={pagBtn(page >= Math.ceil(total / 20))}
                  >
                    Вперёд →
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </main>
  );
}

interface FacetItem {
  code: string;
  label: string;
  count: string;
}

function FacetGroup({
  title,
  items,
  selected,
  onToggle,
}: {
  title: string;
  items: FacetItem[];
  selected: string[];
  onToggle: (code: string) => void;
}) {
  return (
    <div>
      <h3
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: 'var(--navy-700, #0b1736)',
          margin: '0 0 8px',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        {title}
      </h3>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {items.slice(0, 12).map((it) => {
          const on = selected.includes(it.code);
          return (
            <li key={it.code}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  fontSize: 13,
                  cursor: 'pointer',
                  padding: '4px 6px',
                  borderRadius: 6,
                  background: on ? 'var(--bg, #fbfaf7)' : 'transparent',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => onToggle(it.code)}
                    style={{ flexShrink: 0 }}
                  />
                  <span style={{ overflowWrap: 'anywhere' }}>{it.label}</span>
                </span>
                <span style={{ color: 'var(--muted, #6b7287)', fontSize: 12, flexShrink: 0 }}>
                  {Number(it.count).toLocaleString('ru-RU')}
                </span>
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

const RISK_LEVEL_LABELS: Record<string, string> = {
  low: 'Минимальный',
  moderate: 'Умеренный',
  elevated: 'Повышенный',
  high: 'Высокий',
};

function RegistryToggle({
  label,
  note,
  count,
  on,
  onChange,
}: {
  label: string;
  note: string;
  count: string | undefined;
  on: boolean;
  onChange: () => void;
}) {
  return (
    <li>
      <label
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8,
          fontSize: 13,
          cursor: 'pointer',
          padding: '6px 6px',
          borderRadius: 6,
          background: on ? 'var(--bg, #fbfaf7)' : 'transparent',
        }}
      >
        <input
          type="checkbox"
          checked={on}
          onChange={onChange}
          style={{ marginTop: 2, flexShrink: 0 }}
        />
        <span style={{ minWidth: 0, flex: 1, overflowWrap: 'anywhere' }}>
          <div style={{ fontWeight: 500 }}>
            {label}
            {count !== undefined && (
              <span style={{ color: 'var(--muted, #6b7287)', fontSize: 12, marginLeft: 6 }}>
                {Number(count).toLocaleString('ru-RU')}
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted, #6b7287)' }}>{note}</div>
        </span>
      </label>
    </li>
  );
}

const REGISTRY_BADGE_TINTS = {
  blue: { bg: '#e3edff', fg: '#1c4a99', border: '#a8c1ee' },
  green: { bg: '#e6f4e9', fg: '#1f6b34', border: '#9ed4ad' },
  purple: { bg: '#f0e6f7', fg: '#5a2da8', border: '#c5a8e2' },
} as const;

function RegistryBadge({
  tone,
  label,
}: {
  tone: keyof typeof REGISTRY_BADGE_TINTS;
  label: string;
}) {
  const tint = REGISTRY_BADGE_TINTS[tone];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '3px 8px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 500,
        background: tint.bg,
        color: tint.fg,
        border: `1px solid ${tint.border}`,
      }}
    >
      {label}
    </span>
  );
}

function pagBtn(disabled: boolean): React.CSSProperties {
  return {
    padding: '8px 16px',
    background: disabled ? 'var(--border, #e6e2d6)' : 'var(--navy-700, #0b1736)',
    color: disabled ? 'var(--muted, #6b7287)' : '#ffffff',
    border: 'none',
    borderRadius: 8,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 14,
  };
}
